import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { User, PDFDocument, Annotation, Signature, SignatureField, BoxOverlay, DocumentSession } from '@/types';

interface HistoryAction {
  type: 'ADD_SIGNATURE' | 'REMOVE_SIGNATURE' | 'ADD_SIGNATURE_FIELD' | 'REMOVE_SIGNATURE_FIELD' | 'UPDATE_SIGNATURE_FIELD' | 'BULK_ADD_SIGNATURE_FIELDS' | 'BULK_UPDATE_SIGNATURE_FIELDS' | 'BULK_REMOVE_FIELDS' | 'INITIAL_FIELD_TRANSACTION' | 'ADD_ANNOTATION' | 'REMOVE_ANNOTATION' | 'UPDATE_ANNOTATION';
  data: any;
  timestamp: number;
  groupId?: string; // For grouping related operations
}

interface AppState {
  // User state
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;

  // Document state
  currentDocument: PDFDocument | null;
  setCurrentDocument: (document: PDFDocument | null) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  setTotalPages: (total: number) => void;
  zoomLevel: number;
  setZoomLevel: (zoom: number) => void;

  // Collaboration state
  activeUsers: User[];
  setActiveUsers: (users: User[]) => void;
  annotations: Annotation[];
  setAnnotations: (annotations: Annotation[]) => void;
  addAnnotation: (annotation: Annotation) => void;
  updateAnnotation: (id: string, updates: Partial<Annotation>) => void;
  removeAnnotation: (id: string) => void;
  
  signatures: Signature[];
  setSignatures: (signatures: Signature[]) => void;
  addSignature: (signature: Signature) => void;
  updateSignature: (id: string, newSignatureData: string) => Promise<void>;
  removeSignature: (id: string) => void;

  // Saved signatures per user (max 3) and active selection for quick apply
  savedSignatures: Record<string, string[]>; // userId -> dataUrl[]
  addSavedSignature: (userId: string, dataUrl: string) => void;
  removeSavedSignature: (userId: string, index: number) => void;
  activeSignatureDataUrl: string | null;
  setActiveSignatureDataUrl: (dataUrl: string | null) => void;

  // Signature fields
  signatureFields: SignatureField[];
  setSignatureFields: (fields: SignatureField[]) => void;
  loadSignatureFields: (sessionId: string) => Promise<void>;
  loadSignatures: (sessionId: string, siteIdentifier?: string, siteCode?: string) => Promise<void>;
  addSignatureField: (field: SignatureField, sessionId?: string) => void;
  updateSignatureField: (id: string, updates: Partial<SignatureField>, sessionId?: string, skipHistory?: boolean) => void;
  removeSignatureField: (id: string) => void;
  bulkClearFields: (fieldIds: string[], onProgress?: (current: number, total: number, phase: string) => void) => Promise<void>;
  bulkClearFields: (fieldIds: string[], onProgress?: (current: number, total: number, phase: string) => void) => Promise<void>;
  selectedSignatory: string | null;
  setSelectedSignatory: (signatoryId: string | null) => void;

  // Box overlays
  boxOverlays: BoxOverlay[];
  setBoxOverlays: (boxes: BoxOverlay[]) => void;
  addBoxOverlay: (box: BoxOverlay) => void;
  updateBoxOverlay: (id: string, updates: Partial<BoxOverlay>) => void;
  removeBoxOverlay: (id: string) => void;

  // UI state
  selectedTool: 'cursor' | 'highlight' | 'note' | 'drawing' | 'signature' | 'initial' | 'text' | 'box';
  setSelectedTool: (tool: 'cursor' | 'highlight' | 'note' | 'drawing' | 'signature' | 'initial' | 'text' | 'box') => void;
  isAnnotationMode: boolean;
  setIsAnnotationMode: (mode: boolean) => void;
  selectedAnnotation: string | null;
  setSelectedAnnotation: (id: string | null) => void;

  // Loading states
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  loadingMessage: string;
  setLoadingMessage: (message: string) => void;

  // Undo/Redo functionality
  undoHistory: HistoryAction[];
  redoHistory: HistoryAction[];
  addToHistory: (action: HistoryAction) => void;
  startUndoGroup: () => string; // Returns a group ID
  addBulkSignatureFields: (fields: SignatureField[], sessionId?: string) => void;
  bulkUpdateSignatureFields: (updates: Array<{id: string, updates: Partial<SignatureField>}>, sessionId?: string) => void;
  executeInitialFieldTransaction: (field: SignatureField, applyAllPages: boolean, applyUsersAll: boolean, sessionId?: string, skipFirstPage?: boolean, onProgress?: (current: number, total: number, phase: string) => void) => void;
  undoLastAction: () => void;
  redoLastAction: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;

  // Document save functionality
  saveDocumentWithMarkup: (notifyAssigned?: boolean) => Promise<boolean>;

  // Offline mode state
  isOffline: boolean;
  setIsOffline: (offline: boolean) => void;
  pendingOperationsCount: number;
  setPendingOperationsCount: (count: number) => void;
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // User state
      currentUser: null,
      setCurrentUser: (user) => set({ currentUser: user }),

      // Document state
      currentDocument: null,
      setCurrentDocument: (document) => set({ currentDocument: document }),
      currentPage: 1,
      setCurrentPage: (page) => set({ currentPage: page }),
      totalPages: 0,
      setTotalPages: (total) => set({ totalPages: total }),
      zoomLevel: 1,
      setZoomLevel: (zoom) => set({ zoomLevel: zoom }),

      // Collaboration state
      activeUsers: [],
      setActiveUsers: (users) => set({ activeUsers: users }),
      annotations: [],
      setAnnotations: (annotations) => set({ annotations }),
      addAnnotation: (annotation) => {
        const { annotations, addToHistory } = get();
        set({ annotations: [...annotations, annotation] });
        addToHistory({
          type: 'ADD_ANNOTATION',
          data: annotation,
          timestamp: Date.now()
        });
      },
      updateAnnotation: (id, updates) => {
        const { annotations, addToHistory } = get();
        const oldAnnotation = annotations.find((ann) => ann.id === id);
        if (oldAnnotation) {
          addToHistory({
            type: 'UPDATE_ANNOTATION',
            data: { id, oldData: oldAnnotation, newData: updates },
            timestamp: Date.now()
          });
        }
        set({
          annotations: annotations.map((ann) =>
            ann.id === id ? { ...ann, ...updates } : ann
          ),
        });
      },
      removeAnnotation: (id) => {
        const { annotations, addToHistory } = get();
        const annotationToRemove = annotations.find((ann) => ann.id === id);
        if (annotationToRemove) {
          addToHistory({
            type: 'REMOVE_ANNOTATION',
            data: annotationToRemove,
            timestamp: Date.now()
          });
        }
        set({ annotations: annotations.filter((ann) => ann.id !== id) });
      },

      signatures: [],
      setSignatures: (signatures) => set({ signatures }),
      addSignature: (signature) => {
        const { signatures, addToHistory } = get();
        set({ signatures: [...signatures, signature] });
        addToHistory({
          type: 'ADD_SIGNATURE',
          data: signature,
          timestamp: Date.now()
        });
      },
      updateSignature: async (id, newSignatureData) => {
        const { signatures } = get();
        
        // Update in local state first
        const updatedSignatures = signatures.map(sig =>
          sig.id === id ? { ...sig, signatureData: newSignatureData, signedAt: new Date().toISOString() } : sig
        );
        set({ signatures: updatedSignatures });

        // Persist to database
        try {
          // Extract siteIdentifier for tenant routing
          const params = new URLSearchParams(window.location.search);
          const siteIdentifier = params.get('siteIdentifier');
          const siteCode = params.get('siteCode');
          
          const response = await fetch('/api/signatures', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              signatureId: id,
              signatureData: newSignatureData,
              siteIdentifier,
              siteCode
            })
          });

          if (!response.ok) {
            throw new Error('Failed to update signature in database');
          }


        } catch (error) {
          console.error('❌ Failed to update signature:', error);
          // Revert local state on error
          set({ signatures });
          throw error;
        }
      },
      removeSignature: (id) => {
        const { signatures, addToHistory } = get();
        const signatureToRemove = signatures.find(sig => sig.id === id);
        if (signatureToRemove) {
          set({ signatures: signatures.filter((sig) => sig.id !== id) });
          addToHistory({
            type: 'REMOVE_SIGNATURE',
            data: signatureToRemove,
            timestamp: Date.now()
          });
        }
      },

      // Saved signatures per user
      savedSignatures: {},
      addSavedSignature: (userId, dataUrl) => {
        const { savedSignatures } = get();
        const current = savedSignatures[userId] || [];
        const next = [...current, dataUrl].slice(-3); // keep max 3
        set({ savedSignatures: { ...savedSignatures, [userId]: next } });
      },
      removeSavedSignature: (userId, index) => {
        const { savedSignatures } = get();
        const current = savedSignatures[userId] || [];
        if (index < 0 || index >= current.length) return;
        const next = current.filter((_, i) => i !== index);
        set({ savedSignatures: { ...savedSignatures, [userId]: next } });
      },
      activeSignatureDataUrl: null,
      setActiveSignatureDataUrl: (dataUrl) => set({ activeSignatureDataUrl: dataUrl }),

      // Signature fields
      signatureFields: [],
      setSignatureFields: (fields) => set({ signatureFields: fields }),
      loadSignatureFields: async (sessionId: string) => {
        const DEBUG = false;
        if (DEBUG) console.log('🔍 loadSignatureFields called with sessionId:', sessionId);
        try {
          // Get tenant routing params from current URL (if available)
          let url = `/api/signature-fields?sessionId=${sessionId}`;
          if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            const siteIdentifier = params.get('siteIdentifier');
            const siteCode = params.get('siteCode');
            if (siteIdentifier) url += `&siteIdentifier=${encodeURIComponent(siteIdentifier)}`;
            if (siteCode) url += `&siteCode=${encodeURIComponent(siteCode)}`;
          }
          
          const response = await fetch(url);
          if (DEBUG) console.log('📡 loadSignatureFields response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            if (DEBUG) console.log('📊 loadSignatureFields data received:', data);
            set({ signatureFields: data.signatureFields || [] });
            if (DEBUG) console.log('✅ Signature fields loaded:', data.signatureFields?.length || 0);
          } else {
            console.error('❌ Failed to load signature fields - status:', response.status);
            set({ signatureFields: [] });
          }
        } catch (error) {
          console.error('❌ Error loading signature fields:', error);
          set({ signatureFields: [] });
        }
      },
      loadSignatures: async (sessionId: string, siteIdentifier?: string, siteCode?: string) => {
        try {
          // Build query parameters
          const params = new URLSearchParams({ sessionId });
          if (siteIdentifier) params.append('siteIdentifier', siteIdentifier);
          if (siteCode) params.append('siteCode', siteCode);
          
          const response = await fetch(`/api/signatures?${params.toString()}`);
          if (response.ok) {
            const data = await response.json();
            set({ signatures: data.signatures || [] });
          } else {
            console.error('❌ Failed to load signatures - status:', response.status);
            set({ signatures: [] });
          }
        } catch (error) {
          console.error('❌ Error loading signatures:', error);
          set({ signatures: [] });
        }
      },
      addSignatureField: async (field, sessionId?: string) => {


        const { signatureFields, addToHistory, currentDocument } = get();
        set({ signatureFields: [...signatureFields, field] });
        addToHistory({
          type: 'ADD_SIGNATURE_FIELD',
          data: field,
          timestamp: Date.now()
        });

        // Save to database via API
        try {
          const finalSessionId = sessionId || currentDocument?.id; // Use provided sessionId or fallback

          if (finalSessionId) {

            // Get tenant routing params from current URL (if available)
            let siteIdentifier, siteCode;
            if (typeof window !== 'undefined') {
              const params = new URLSearchParams(window.location.search);
              siteIdentifier = params.get('siteIdentifier') || undefined;
              siteCode = params.get('siteCode') || undefined;
            }
            
            const response = await fetch('/api/signature-fields', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId: finalSessionId,
                signatureField: field,
                siteIdentifier,
                siteCode,
              }),
            });


            const responseData = await response.json();


            if (!response.ok) {
              console.error('❌ Failed to save signature field to database:', responseData);
            } else {

            }
          } else {
            console.warn('⚠️ No session ID found, cannot save signature field');
          }
        } catch (error) {
          console.error('❌ Error saving signature field:', error);
        }
      },
      updateSignatureField: async (id, updates, sessionId?: string, skipHistory?: boolean) => {

        const { signatureFields, addToHistory, currentDocument } = get();
        const fieldToUpdate = signatureFields.find(field => field.id === id);
        
        // Only add to history if not skipping (e.g., during drag/resize, we skip until drop)
        if (fieldToUpdate && !skipHistory) {
          addToHistory({
            type: 'UPDATE_SIGNATURE_FIELD',
            data: {
              id,
              previousState: { ...fieldToUpdate },
              newState: { ...fieldToUpdate, ...updates }
            },
            timestamp: Date.now()
          });
        }
        
        set({
          signatureFields: signatureFields.map((field) =>
            field.id === id ? { ...field, ...updates } : field
          ),
        });

        // Update in database via API
        try {
          const finalSessionId = sessionId || currentDocument?.id; // Use provided sessionId or fallback

          if (finalSessionId) {

            // Get tenant routing params from current URL (if available)
            let siteIdentifier = null;
            let siteCode = null;
            if (typeof window !== 'undefined') {
              const params = new URLSearchParams(window.location.search);
              siteIdentifier = params.get('siteIdentifier');
              siteCode = params.get('siteCode');

            }
            
            const response = await fetch('/api/signature-fields', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId: finalSessionId,
                signatureFieldId: id,
                updates,
                siteIdentifier,
                siteCode,
              }),
            });


            
            if (!response.ok) {
              const errorText = await response.text();
              console.error('❌ Failed to update signature field in database:', response.status, errorText.substring(0, 200));
            } else {
              const responseData = await response.json();


            }
          } else {
            console.warn('⚠️ No session ID found, cannot update signature field');
          }
        } catch (error) {
          console.error('❌ Error updating signature field:', error);
        }
      },
      removeSignatureField: async (id) => {
        const { signatureFields, signatures, addToHistory, currentDocument } = get();
        const fieldToRemove = signatureFields.find(field => field.id === id);
        if (fieldToRemove) {
          // Remove the field from state
          set({ signatureFields: signatureFields.filter((field) => field.id !== id) });
          
          // Remove only signatures associated with THIS field
          set({ signatures: signatures.filter(sig => sig.fieldId !== id) });
          
          addToHistory({
            type: 'REMOVE_SIGNATURE_FIELD',
            data: fieldToRemove,
            timestamp: Date.now()
          });

          // Delete from database via API (async, no need to wait)
          try {
            let url = `/api/signature-fields?id=${id}`;
            if (typeof window !== 'undefined') {
              const params = new URLSearchParams(window.location.search);
              const siteIdentifier = params.get('siteIdentifier');
              const siteCode = params.get('siteCode');
              if (siteIdentifier) url += `&siteIdentifier=${encodeURIComponent(siteIdentifier)}`;
              if (siteCode) url += `&siteCode=${encodeURIComponent(siteCode)}`;
            }

            fetch(url, { method: 'DELETE' })
              .then(response => {
                if (!response.ok) {
                  console.error('Failed to delete signature field from database');
                }
              })
              .catch(error => {
                console.error('Error deleting signature field:', error);
              });
          } catch (error) {
            console.error('Error deleting signature field:', error);
          }
        }
      },

      bulkClearFields: async (fieldIds: string[], onProgress?: (current: number, total: number, phase: string) => void) => {
        const { signatureFields, addToHistory } = get();
        
        if (fieldIds.length === 0) return;
        

        onProgress?.(0, fieldIds.length, 'Preparing to clear fields...');
        
        try {
          // First, remove all fields from state immediately for better UX
          const fieldsToRemove = signatureFields.filter(field => fieldIds.includes(field.id));
          const remainingFields = signatureFields.filter(field => !fieldIds.includes(field.id));
          
          set({ signatureFields: remainingFields });
          
          // Add to history as a single bulk operation
          addToHistory({
            type: 'BULK_REMOVE_FIELDS',
            data: fieldsToRemove,
            timestamp: Date.now()
          });
          
          onProgress?.(0, fieldIds.length, 'Clearing fields from database...');
          
          // Extract tenant routing params
          let siteIdentifier = null;
          let siteCode = null;
          if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            siteIdentifier = params.get('siteIdentifier');
            siteCode = params.get('siteCode');
          }
          
          // Process deletions in batches to avoid overwhelming the server
          const BATCH_SIZE = 100;
          let processedCount = 0;
          const startTime = Date.now();
          const TIMEOUT_MS = 300000; // 5 minutes timeout
          
          for (let i = 0; i < fieldIds.length; i += BATCH_SIZE) {
            const batch = fieldIds.slice(i, i + BATCH_SIZE);
            const batchNum = Math.floor(i / BATCH_SIZE) + 1;
            const totalBatches = Math.ceil(fieldIds.length / BATCH_SIZE);
            

            onProgress?.(processedCount, fieldIds.length, `Deleting batch ${batchNum}/${totalBatches}...`);
            
            // Check for timeout
            const elapsed = Date.now() - startTime;
            if (elapsed > TIMEOUT_MS) {
              throw new Error(`Clear operation timed out after ${Math.round(elapsed / 1000)}s. Processed ${processedCount}/${fieldIds.length} fields.`);
            }
            
            // Create API calls for this batch
            const deletePromises = batch.map(id => {
              let url = `/api/signature-fields?id=${id}`;
              if (siteIdentifier) url += `&siteIdentifier=${encodeURIComponent(siteIdentifier)}`;
              if (siteCode) url += `&siteCode=${encodeURIComponent(siteCode)}`;
              
              return fetch(url, { method: 'DELETE' })
                .then(response => {
                  if (!response.ok) {
                    console.warn(`⚠️ Failed to delete field ${id}: ${response.status}`);
                  }
                  return { id, success: response.ok };
                })
                .catch(error => {
                  console.error(`❌ Error deleting field ${id}:`, error);
                  return { id, success: false };
                });
            });
            
            // Execute batch with error handling
            const results = await Promise.allSettled(deletePromises);
            const failed = results.filter(r => r.status === 'rejected' || 
              (r.status === 'fulfilled' && !r.value.success)).length;
            
            if (failed > 0) {
              console.warn(`⚠️ ${failed}/${batch.length} field deletions failed in this batch`);
            }
            
            processedCount += batch.length;
            
            // Small delay between batches
            if (i + BATCH_SIZE < fieldIds.length) {
              await new Promise(resolve => setTimeout(resolve, 50));
            }
          }
          

          
        } catch (error) {
          console.error('❌ Bulk clear failed:', error);
          throw error;
        }
      },
      selectedSignatory: null,
      setSelectedSignatory: (signatoryId) => set({ selectedSignatory: signatoryId }),

      // Box overlays
      boxOverlays: [],
      setBoxOverlays: (boxes) => set({ boxOverlays: boxes }),
      addBoxOverlay: (box) => {
        const { boxOverlays } = get();
        set({ boxOverlays: [...boxOverlays, box] });
      },
      updateBoxOverlay: (id, updates) => {
        const { boxOverlays } = get();
        set({ boxOverlays: boxOverlays.map(b => b.id === id ? { ...b, ...updates, updatedAt: new Date().toISOString() } : b) });
      },
      removeBoxOverlay: (id) => {
        const { boxOverlays } = get();
        set({ boxOverlays: boxOverlays.filter(b => b.id !== id) });
      },

      // UI state
      selectedTool: 'signature',
      setSelectedTool: (tool) => set({ selectedTool: tool }),
      isAnnotationMode: false,
      setIsAnnotationMode: (mode) => set({ isAnnotationMode: mode }),
      selectedAnnotation: null,
      setSelectedAnnotation: (id) => set({ selectedAnnotation: id }),

      // Loading states
      isLoading: false,
      setIsLoading: (loading) => set({ isLoading: loading }),
      loadingMessage: '',
      setLoadingMessage: (message) => set({ loadingMessage: message }),

      // Undo/Redo functionality
      undoHistory: [],
      redoHistory: [],
      
      addToHistory: (action) => {
        const { undoHistory } = get();
        const newHistory = [...undoHistory, action];
        // Keep only the last 25 actions
        if (newHistory.length > 25) {
          newHistory.shift();
        }
        set({ 
          undoHistory: newHistory,
          redoHistory: [] // Clear redo history when new action is added
        });
      },

      startUndoGroup: () => {
        return `group_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      },

      addBulkSignatureFields: async (fields: SignatureField[], sessionId?: string) => {

        const { signatureFields, addToHistory, currentDocument } = get();
        
        // Add all fields to state at once
        set({ signatureFields: [...signatureFields, ...fields] });
        
        // Add single undo action for all fields
        addToHistory({
          type: 'BULK_ADD_SIGNATURE_FIELDS',
          data: fields,
          timestamp: Date.now()
        });

        // Save to database via API for each field
        try {
          const finalSessionId = sessionId || currentDocument?.id;
          if (finalSessionId) {

            
            // Process in parallel for better performance
            // Get tenant routing params from current URL (if available)
            let siteIdentifier, siteCode;
            if (typeof window !== 'undefined') {
              const params = new URLSearchParams(window.location.search);
              siteIdentifier = params.get('siteIdentifier') || undefined;
              siteCode = params.get('siteCode') || undefined;
            }
            
            const savePromises = fields.map((field: SignatureField) => 
              fetch('/api/signature-fields', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sessionId: finalSessionId,
                  signatureField: field,
                  siteIdentifier,
                  siteCode,
                }),
              })
            );

            const responses = await Promise.allSettled(savePromises);
            const successCount = responses.filter(r => r.status === 'fulfilled').length;

          } else {
            console.warn('⚠️ No session ID found, cannot save signature fields');
          }
        } catch (error) {
          console.error('❌ Error saving bulk signature fields:', error);
        }
      },

      bulkUpdateSignatureFields: async (updates: Array<{id: string, updates: Partial<SignatureField>}>, sessionId?: string) => {

        const { signatureFields, addToHistory, currentDocument } = get();
        
        // Store previous states for undo
        const previousStates = updates.map((update: {id: string, updates: Partial<SignatureField>}) => {
          const field = signatureFields.find((f: SignatureField) => f.id === update.id);
          return { id: update.id, previousState: field ? { ...field } : null };
        }).filter((item: any) => item.previousState !== null);

        // Apply all updates to state at once
        const updatedFields = signatureFields.map((field: SignatureField) => {
          const update = updates.find((u: {id: string, updates: Partial<SignatureField>}) => u.id === field.id);
          return update ? { ...field, ...update.updates } : field;
        });
        set({ signatureFields: updatedFields });

        // Add single undo action for all updates
        addToHistory({
          type: 'BULK_UPDATE_SIGNATURE_FIELDS',
          data: { updates, previousStates },
          timestamp: Date.now()
        });

        // Save to database via API for each update
        try {
          const finalSessionId = sessionId || currentDocument?.id;
          if (finalSessionId) {

            
            const updatePromises = updates.map(({ id, updates: fieldUpdates }: {id: string, updates: Partial<SignatureField>}) => 
              fetch('/api/signature-fields', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sessionId: finalSessionId,
                  fieldId: id,
                  updates: fieldUpdates,
                }),
              })
            );

            const responses = await Promise.allSettled(updatePromises);
            const successCount = responses.filter(r => r.status === 'fulfilled').length;

          } else {
            console.warn('⚠️ No session ID found, cannot update signature fields');
          }
        } catch (error) {
          console.error('❌ Error updating bulk signature fields:', error);
        }
      },

      executeInitialFieldTransaction: async (field: SignatureField, applyAllPages: boolean, applyUsersAll: boolean, sessionId?: string, skipFirstPage: boolean = false, onProgress?: (current: number, total: number, phase: string) => void) => {

        const { signatureFields, currentDocument, totalPages, addToHistory } = get();
        
        // Extract tenant routing params from URL
        let siteIdentifier = null;
        let siteCode = null;
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          siteIdentifier = params.get('siteIdentifier');
          siteCode = params.get('siteCode');

        }
        
        // Store all the state changes for undo
        const transactionData: any = {
          originalField: { ...field },
          addedFields: [] as SignatureField[],
          updatedFields: [] as { id: string, previousState: SignatureField, newState: SignatureField }[],
          fieldStatusUpdate: { id: field.id, previousStatus: field.status, newStatus: 'pending' }
        };

        // 1. Add/Update fields to all pages if requested
        if (applyAllPages) {
          const cloneFields: SignatureField[] = [];
          const fieldsToUpdate: { id: string, previousState: SignatureField, newState: SignatureField }[] = [];
          
          // Helper function to check if two fields overlap (reusing overlap detection logic)
          const checkOverlap = (field1: any, field2: any): boolean => {
            if (field1.pageNumber !== field2.pageNumber) return false;
            
            const f1 = field1.position || field1;
            const f2 = field2.position || field2;
            
            const horizontalOverlap = (f1.x < f2.x + f2.width) && (f1.x + f1.width > f2.x);
            const verticalOverlap = (f1.y < f2.y + f2.height) && (f1.y + f1.height > f2.y);
            
            return horizontalOverlap && verticalOverlap;
          };
          
          // Determine starting page based on skipFirstPage option
          const startPage = skipFirstPage ? 2 : 1;
          const totalPagesToProcess = totalPages - startPage + 1;
          
          // Initial progress update
          onProgress?.(0, totalPagesToProcess, 'Analyzing pages...');
          
          for (let p = startPage; p <= totalPages; p++) {
            if (p === field.pageNumber) continue;
            
            // Update progress
            const currentProgress = p - startPage + 1;
            onProgress?.(currentProgress, totalPagesToProcess, `Processing page ${p}...`);
            
            // Check if a field of the same type overlaps with where we want to place the new field
            const overlappingField = signatureFields.find((f: SignatureField) => {
              if (f.fieldType !== field.fieldType || f.pageNumber !== p) return false;
              
              const overlaps = checkOverlap(
                { position: field.position, pageNumber: p },
                f
              );
              
              if (overlaps) {
              }
              
              return overlaps;
            });
            
            if (overlappingField) {
              // Update existing field instead of creating duplicate
              const updatedField = {
                ...overlappingField,
                assignedUserIds: field.assignedUserIds,
                position: field.position, // Update position to exact match
                status: 'pending'
              };
              
              fieldsToUpdate.push({
                id: overlappingField.id,
                previousState: { ...overlappingField },
                newState: updatedField
              });
            } else {

              // Create new field - no overlap detected
              const clone: SignatureField = {
                ...field,
                id: `${field.fieldType}_${Date.now()}_${Math.random().toString(36).slice(2,9)}`,
                pageNumber: p,
                createdAt: new Date().toISOString(),
                status: 'pending',
              };
              cloneFields.push(clone);
            }
          }
          
          transactionData.addedFields = cloneFields;
          
          // Apply updates to existing fields and add new fields
          const updatedFields = signatureFields.map((f: SignatureField) => {
            const update = fieldsToUpdate.find(u => u.id === f.id);
            return update ? update.newState : f;
          });
          
          // Combine updated existing fields with new clone fields
          set({ signatureFields: [...updatedFields, ...cloneFields] });
          
          // Save updates to database for existing fields (batched)
          const finalSessionId = sessionId || currentDocument?.id;
          if (finalSessionId && fieldsToUpdate.length > 0) {

            
            // Process updates in batches (optimized for large docs)
            const BATCH_SIZE = 100;
            for (let i = 0; i < fieldsToUpdate.length; i += BATCH_SIZE) {
              const batch = fieldsToUpdate.slice(i, i + BATCH_SIZE);
              const batchNum = Math.floor(i / BATCH_SIZE) + 1;
              const totalBatches = Math.ceil(fieldsToUpdate.length / BATCH_SIZE);
              

              onProgress?.(totalPagesToProcess + i, totalPagesToProcess + fieldsToUpdate.length, `Updating batch ${batchNum}/${totalBatches}...`);
              
              const updatePromises = batch.map(({ id, newState }) =>
                fetch('/api/signature-fields', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sessionId: finalSessionId,
                    signatureFieldId: id,
                    updates: { 
                      assignedUserIds: newState.assignedUserIds,
                      position: newState.position,
                      status: newState.status
                    },
                    siteIdentifier,
                    siteCode,
                  }),
                }).then(response => {
                  if (!response.ok) {
                    console.warn(`⚠️ Field update failed for ${id}: ${response.status}`);
                  }
                  return response;
                }).catch(error => {
                  console.error('❌ Error updating existing field:', error);
                  return null;
                })
              );
              
              const results = await Promise.allSettled(updatePromises);
              const failed = results.filter(r => r.status === 'rejected').length;
              if (failed > 0) {
                console.warn(`⚠️ ${failed}/${batch.length} field updates failed in this batch`);
              }
              
              // Reduced delay between batches
              if (i + BATCH_SIZE < fieldsToUpdate.length) {
                await new Promise(resolve => setTimeout(resolve, 50));
              }
            }
          }
          
          // Save new fields to database (batched)
          if (finalSessionId && cloneFields.length > 0) {

            
            // Process new fields in batches (optimized for large docs)
            const BATCH_SIZE = 100;
            for (let i = 0; i < cloneFields.length; i += BATCH_SIZE) {
              const batch = cloneFields.slice(i, i + BATCH_SIZE);
              const batchNum = Math.floor(i / BATCH_SIZE) + 1;
              const totalBatches = Math.ceil(cloneFields.length / BATCH_SIZE);
              

              const currentProgress = totalPagesToProcess + fieldsToUpdate.length + i;
              const totalOperations = totalPagesToProcess + fieldsToUpdate.length + cloneFields.length;
              onProgress?.(currentProgress, totalOperations, `Creating batch ${batchNum}/${totalBatches}...`);
              
              const createPromises = batch.map(clone =>
                fetch('/api/signature-fields', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sessionId: finalSessionId,
                    signatureField: clone,
                    siteIdentifier,
                    siteCode,
                  }),
                }).then(response => {
                  if (!response.ok) {
                    console.warn(`⚠️ Field creation failed: ${response.status}`);
                  }
                  return response;
                }).catch(error => {
                  console.error('❌ Error saving clone field:', error);
                  return null;
                })
              );
              
              const results = await Promise.allSettled(createPromises);
              const failed = results.filter(r => r.status === 'rejected').length;
              if (failed > 0) {
                console.warn(`⚠️ ${failed}/${batch.length} field creations failed in this batch`);
              }
              
              // Reduced delay between batches
              if (i + BATCH_SIZE < cloneFields.length) {
                await new Promise(resolve => setTimeout(resolve, 50));
              }
            }
          }
          

        }

        // 2. Apply users to all fields of the same type if requested
        if (applyUsersAll) {
          const currentFields = applyAllPages ? [...signatureFields, ...transactionData.addedFields] : signatureFields;
          const fieldsToUpdate = currentFields.filter((x: SignatureField) => x.fieldType === field.fieldType);
          
          fieldsToUpdate.forEach((f: SignatureField) => {
            if (f.id !== field.id && (!f.assignedUserIds || JSON.stringify(f.assignedUserIds) !== JSON.stringify(field.assignedUserIds))) {
              transactionData.updatedFields.push({
                id: f.id,
                previousState: { ...f },
                newState: { ...f, assignedUserIds: field.assignedUserIds }
              });
            }
          });

          // Apply updates to state
          if (transactionData.updatedFields.length > 0) {
            const updatedFields = currentFields.map((f: SignatureField) => {
              const update = transactionData.updatedFields.find((u: any) => u.id === f.id);
              return update ? update.newState : f;
            });
            set({ signatureFields: updatedFields });

            // Save updates to database (batched)
            const finalSessionId = sessionId || currentDocument?.id;
            if (finalSessionId) {

              
              // Process user assignment updates in batches (optimized for large docs)
              const BATCH_SIZE = 100;
              for (let i = 0; i < transactionData.updatedFields.length; i += BATCH_SIZE) {
                const batch = transactionData.updatedFields.slice(i, i + BATCH_SIZE);
                const batchNum = Math.floor(i / BATCH_SIZE) + 1;
                const totalBatches = Math.ceil(transactionData.updatedFields.length / BATCH_SIZE);
                

                const baseProgress = totalPagesToProcess + fieldsToUpdate.length + cloneFields.length;
                onProgress?.(baseProgress + i, baseProgress + transactionData.updatedFields.length, `Assigning users batch ${batchNum}/${totalBatches}...`);
                
                const updatePromises = batch.map(({ id, newState }: any) =>
                  fetch('/api/signature-fields', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      sessionId: finalSessionId,
                      signatureFieldId: id,
                      updates: { 
                        assignedUserIds: newState.assignedUserIds,
                        status: newState.status
                      },
                    }),
                  }).then(response => {
                    if (!response.ok) {
                      console.warn(`⚠️ User assignment update failed for ${id}: ${response.status}`);
                    }
                    return response;
                  }).catch(error => {
                    console.error('❌ Error updating field:', error);
                    return null;
                  })
                );
                
                const results = await Promise.allSettled(updatePromises);
                const failed = results.filter(r => r.status === 'rejected').length;
                if (failed > 0) {
                  console.warn(`⚠️ ${failed}/${batch.length} user assignment updates failed in this batch`);
                }
                
                // Reduced delay between batches
                if (i + BATCH_SIZE < transactionData.updatedFields.length) {
                  await new Promise(resolve => setTimeout(resolve, 50));
                }
              }
            }
          }
        }

        // 3. Update original field status (or remove it if it's on page 1 and skipFirstPage is true)
        const { signatureFields: currentSignatureFields } = get();
        
        if (skipFirstPage && field.pageNumber === 1) {
          // Remove the field from page 1

          set({
            signatureFields: currentSignatureFields.filter((f: SignatureField) => f.id !== field.id)
          });
          
          // Delete from database
          const finalSessionId = sessionId || currentDocument?.id;
          if (finalSessionId) {
            fetch(`/api/signature-fields?sessionId=${finalSessionId}&fieldId=${field.id}`, {
              method: 'DELETE',
            }).catch(error => console.error('❌ Error deleting page 1 field:', error));
          }
          
          transactionData.removedOriginalField = true;
        } else {
          // Update status normally
          set({
            signatureFields: currentSignatureFields.map((f: SignatureField) =>
              f.id === field.id ? { ...f, status: 'pending' } : f
            )
          });

          // Save original field status to database
          const finalSessionId = sessionId || currentDocument?.id;
          if (finalSessionId) {
            fetch('/api/signature-fields', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId: finalSessionId,
                signatureFieldId: field.id,
                updates: { status: 'pending' },
                siteIdentifier,
                siteCode,
              }),
            }).catch(error => console.error('❌ Error updating original field status:', error));
          }
        }

        // 4. Add single transaction to undo history
        addToHistory({
          type: 'INITIAL_FIELD_TRANSACTION',
          data: transactionData,
          timestamp: Date.now()
        });


      },

      undoLastAction: () => {
        const { undoHistory, redoHistory } = get();
        if (undoHistory.length === 0) return;

        const lastAction = undoHistory[undoHistory.length - 1];
        const newUndoHistory = undoHistory.slice(0, -1);
        const newRedoHistory = [...redoHistory, lastAction];

        // Keep only the last 20 redo actions
        if (newRedoHistory.length > 20) {
          newRedoHistory.shift();
        }

        set({
          undoHistory: newUndoHistory,
          redoHistory: newRedoHistory
        });

        // Execute the undo action
        const state = get();
        const { currentDocument, signatureFields } = state;
        switch (lastAction.type) {
          case 'ADD_SIGNATURE':
            // Remove signature without adding to history to avoid infinite loop
            const { signatures } = state;
            const signatureToDelete = lastAction.data;
            set({ signatures: signatures.filter(sig => sig.id !== signatureToDelete.id) });
            
            // Also revert the field status back to 'pending' since signature was undone
            if (signatureToDelete.fieldId) {
              const relatedField = signatureFields.find(f => f.id === signatureToDelete.fieldId);
              if (relatedField && relatedField.status === 'completed') {
                set({
                  signatureFields: signatureFields.map(field =>
                    field.id === signatureToDelete.fieldId ? { ...field, status: 'pending' } : field
                  )
                });
                
                // Update field status on server as well
                (async () => {
                  try {
                    const sessionId = currentDocument?.id;
                    if (sessionId) {
                      let url = '/api/signature-fields';
                      if (typeof window !== 'undefined') {
                        const params = new URLSearchParams(window.location.search);
                        const siteIdentifier = params.get('siteIdentifier');
                        const siteCode = params.get('siteCode');
                        if (siteIdentifier || siteCode) {
                          url += '?';
                          if (siteIdentifier) url += `siteIdentifier=${encodeURIComponent(siteIdentifier)}`;
                          if (siteCode) url += `${siteIdentifier ? '&' : ''}siteCode=${encodeURIComponent(siteCode)}`;
                        }
                      }

                      await fetch(url, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          sessionId,
                          signatureFieldId: signatureToDelete.fieldId,
                          updates: { status: 'pending' },
                        }),
                      });

                    }
                  } catch (error) {
                    console.error('❌ Error reverting field status on server:', error);
                  }
                })();
              }
            }
            
            // Also delete signature from server to prevent duplication on reload
            (async () => {
              try {

                const response = await fetch(`/api/signatures?id=${encodeURIComponent(signatureToDelete.id)}`, {
                  method: 'DELETE',
                });
                if (!response.ok) {
                  console.error('❌ Failed to delete signature from database during undo');
                } else {

                }
              } catch (error) {
                console.error('❌ Error deleting signature during undo:', error);
              }
            })();
            break;
          case 'REMOVE_SIGNATURE':
            const { signatures: currentSignatures } = state;
            const signatureToRestore = lastAction.data;
            set({ signatures: [...currentSignatures, signatureToRestore] });
            
            // Also restore the field status to 'completed' since signature was restored
            if (signatureToRestore.fieldId) {
              const relatedField = signatureFields.find(f => f.id === signatureToRestore.fieldId);
              if (relatedField && relatedField.status === 'pending') {
                set({
                  signatureFields: signatureFields.map(field =>
                    field.id === signatureToRestore.fieldId ? { ...field, status: 'completed' } : field
                  )
                });
                
                // Update field status on server as well
                (async () => {
                  try {
                    const sessionId = currentDocument?.id;
                    if (sessionId) {
                      await fetch('/api/signature-fields', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          sessionId,
                          signatureFieldId: signatureToRestore.fieldId,
                          updates: { status: 'completed' },
                        }),
                      });

                    }
                  } catch (error) {
                    console.error('❌ Error restoring field status on server during undo:', error);
                  }
                })();
              }
            }
            
            // Also restore signature to server
            (async () => {
              try {
                const sessionId = currentDocument?.id;
                if (!sessionId) {
                  console.warn('⚠️ No session ID found for signature restoration');
                  return;
                }
                

                const response = await fetch('/api/signatures', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sessionId,
                    signature: signatureToRestore,
                    type: signatureToRestore.fieldType
                  }),
                });
                if (!response.ok) {
                  console.error('❌ Failed to restore signature to database during undo');
                } else {

                }
              } catch (error) {
                console.error('❌ Error restoring signature during undo:', error);
              }
            })();
            break;
          case 'ADD_SIGNATURE_FIELD':
            set({ signatureFields: signatureFields.filter(field => field.id !== lastAction.data.id) });
            break;
          case 'BULK_ADD_SIGNATURE_FIELDS':
            // Remove all fields that were added in the bulk operation
            const { signatureFields: currentFieldsForBulkRemove } = state;
            const fieldsToRemove = lastAction.data as SignatureField[];
            const fieldIdsToRemove = new Set(fieldsToRemove.map(f => f.id));
            set({ 
              signatureFields: currentFieldsForBulkRemove.filter(field => !fieldIdsToRemove.has(field.id)) 
            });
            break;
          case 'REMOVE_SIGNATURE_FIELD':
            const { signatureFields: currentFields } = state;
            set({ signatureFields: [...currentFields, lastAction.data] });
            break;
          case 'UPDATE_SIGNATURE_FIELD':
            const { signatureFields: fieldsToUpdate } = state;
            set({
              signatureFields: fieldsToUpdate.map(field =>
                field.id === lastAction.data.id ? { ...field, ...lastAction.data.previousState } : field
              )
            });
            break;
          case 'BULK_UPDATE_SIGNATURE_FIELDS':
            // Restore all fields to their previous states
            const { signatureFields: fieldsToRevert } = state;
            const { previousStates } = lastAction.data;
            set({
              signatureFields: fieldsToRevert.map((field: SignatureField) => {
                const previousState = previousStates.find((p: any) => p.id === field.id);
                return previousState ? { ...field, ...previousState.previousState } : field;
              })
            });
            break;
          case 'INITIAL_FIELD_TRANSACTION':
            // Undo all parts of the transaction
            const { signatureFields: currentTransactionFields } = state;
            const transactionData = lastAction.data;
            
            // 1. Remove added fields
            let restoredFields = currentTransactionFields;
            const fieldsToDeleteFromServer: string[] = [];
            
            if (transactionData.addedFields && transactionData.addedFields.length > 0) {
              const addedFieldIds = new Set(transactionData.addedFields.map((f: SignatureField) => f.id));
              fieldsToDeleteFromServer.push(...transactionData.addedFields.map((f: SignatureField) => f.id));
              restoredFields = restoredFields.filter(field => !addedFieldIds.has(field.id));
            }
            
            // 2. Restore updated fields to their previous states  
            const fieldsToUpdateOnServer: Array<{id: string, updates: Partial<SignatureField>}> = [];
            
            if (transactionData.updatedFields && transactionData.updatedFields.length > 0) {
              restoredFields = restoredFields.map(field => {
                const update = transactionData.updatedFields.find((u: any) => u.id === field.id);
                if (update) {
                  // Track what needs to be restored on server
                  fieldsToUpdateOnServer.push({
                    id: field.id,
                    updates: { assignedUserIds: update.previousState.assignedUserIds }
                  });
                  return update.previousState;
                }
                return field;
              });
            }
            
            // 3. Restore original field status
            if (transactionData.fieldStatusUpdate) {
              restoredFields = restoredFields.map(field => 
                field.id === transactionData.fieldStatusUpdate.id 
                  ? { ...field, status: transactionData.fieldStatusUpdate.previousStatus }
                  : field
              );
              
              // Track original field status restoration for server
              fieldsToUpdateOnServer.push({
                id: transactionData.fieldStatusUpdate.id,
                updates: { status: transactionData.fieldStatusUpdate.previousStatus }
              });
            }
            
            // Update local state immediately
            set({ signatureFields: restoredFields });
            
            // Perform server operations asynchronously
            const performServerUndo = async () => {
              const sessionId = currentDocument?.id;
              if (!sessionId) {
                console.warn('⚠️ No session ID found for server undo operations');
                return;
              }
              
              try {
                // Delete added fields from server
                if (fieldsToDeleteFromServer.length > 0) {

                  const deletePromises = fieldsToDeleteFromServer.map(fieldId =>
                    fetch(`/api/signature-fields?id=${encodeURIComponent(fieldId)}`, {
                      method: 'DELETE',
                    }).catch(error => console.error('❌ Error deleting field:', fieldId, error))
                  );
                  await Promise.allSettled(deletePromises);
                }
                
                // Update restored fields on server
                if (fieldsToUpdateOnServer.length > 0) {

                  const updatePromises = fieldsToUpdateOnServer.map(({ id, updates }) =>
                    fetch('/api/signature-fields', {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ sessionId, signatureFieldId: id, updates }),
                    }).catch(error => console.error('❌ Error updating field:', id, error))
                  );
                  await Promise.allSettled(updatePromises);
                }
                

              } catch (error) {
                console.error('❌ Error during server undo operations:', error);
              }
            };
            
            // Execute server operations without blocking UI
            performServerUndo();
            break;

          case 'ADD_ANNOTATION':
            // Remove annotation without adding to history
            const annotationToDelete = lastAction.data;
            const { annotations: currentAnnotations, currentUser, currentDocument } = state;
            // Use spread to force new array reference and trigger re-render
            set({ annotations: [...currentAnnotations.filter(ann => ann.id !== annotationToDelete.id)] });
            
            // Delete from database
            (async () => {
              try {

                const urlParams = new URLSearchParams(window.location.search);
                const siteIdentifier = urlParams.get('siteIdentifier');
                const siteCode = urlParams.get('siteCode');
                
                const params = new URLSearchParams({
                  id: annotationToDelete.id,
                  userId: annotationToDelete.userId || currentUser?.id || '',
                  isAdmin: 'false'
                });
                if (siteIdentifier) params.append('siteIdentifier', siteIdentifier);
                if (siteCode) params.append('siteCode', siteCode);
                
                await fetch(`/api/annotations?${params.toString()}`, {
                  method: 'DELETE',
                });

              } catch (error) {
                console.error('❌ Error deleting annotation:', error);
              }
            })();
            break;

          case 'REMOVE_ANNOTATION':
            // Restore annotation without adding to history
            const annotationToRestore = lastAction.data;
            const { annotations: annotationsForRestore, currentDocument: currentDocForRestore } = state;
            // Use spread to force new array reference and trigger re-render
            set({ annotations: [...annotationsForRestore, annotationToRestore] });
            
            // Restore to database
            (async () => {
              try {

                const urlParams = new URLSearchParams(window.location.search);
                const siteIdentifier = urlParams.get('siteIdentifier');
                const siteCode = urlParams.get('siteCode');
                
                // Transform to match API expected field names
                const payload = {
                  id: annotationToRestore.id,
                  sessionId: annotationToRestore.sessionId || currentDocForRestore?.id,
                  documentId: annotationToRestore.documentId,
                  user_id: annotationToRestore.userId,
                  pageNumber: annotationToRestore.pageNumber,
                  type: annotationToRestore.type,
                  content: annotationToRestore.content,
                  position: annotationToRestore.position,
                  color: annotationToRestore.color,
                  strokeWidth: annotationToRestore.strokeWidth,
                  visibility: annotationToRestore.visibility
                };
                if (siteIdentifier) payload.siteIdentifier = siteIdentifier;
                if (siteCode) payload.siteCode = siteCode;
                
                await fetch('/api/annotations', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });

              } catch (error) {
                console.error('❌ Error restoring annotation:', error);
              }
            })();
            break;

          case 'UPDATE_ANNOTATION':
            // Revert annotation to old data
            const { id: annId, oldData } = lastAction.data;
            const { annotations: annotationsForUpdate } = state;
            set({
              annotations: annotationsForUpdate.map(ann =>
                ann.id === annId ? { ...ann, ...oldData } : ann
              )
            });
            
            // Update in database
            (async () => {
              try {

                const urlParams = new URLSearchParams(window.location.search);
                const siteIdentifier = urlParams.get('siteIdentifier');
                const siteCode = urlParams.get('siteCode');
                
                const payload: any = {
                  id: annId,
                  updates: oldData,
                };
                if (siteIdentifier) payload.siteIdentifier = siteIdentifier;
                if (siteCode) payload.siteCode = siteCode;
                
                await fetch('/api/annotations', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });

              } catch (error) {
                console.error('❌ Error reverting annotation:', error);
              }
            })();
            break;
          
          case 'BULK_REMOVE_FIELDS':
            // Restore all removed fields
            const { signatureFields: currentFieldsForBulkRestore } = state;
            const fieldsToRestore = lastAction.data; // Array of SignatureField objects
            set({ 
              signatureFields: [...currentFieldsForBulkRestore, ...fieldsToRestore] 
            });
            
            // Restore fields to database asynchronously
            (async () => {
              try {
                const sessionId = currentDocument?.id;
                if (!sessionId) {
                  console.warn('⚠️ No session ID found for bulk restore operations');
                  return;
                }
                
                // Extract tenant routing params
                let siteIdentifier = null;
                let siteCode = null;
                if (typeof window !== 'undefined') {
                  const params = new URLSearchParams(window.location.search);
                  siteIdentifier = params.get('siteIdentifier');
                  siteCode = params.get('siteCode');
                }
                

                
                // Process in batches to avoid overwhelming the server
                const BATCH_SIZE = 100;
                for (let i = 0; i < fieldsToRestore.length; i += BATCH_SIZE) {
                  const batch = fieldsToRestore.slice(i, i + BATCH_SIZE);
                  
                  const restorePromises = batch.map((field: SignatureField) => {
                    const payload: any = {
                      sessionId,
                      signatureField: field,
                    };
                    if (siteIdentifier) payload.siteIdentifier = siteIdentifier;
                    if (siteCode) payload.siteCode = siteCode;
                    
                    return fetch('/api/signature-fields', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload),
                    }).catch(error => {
                      console.error(`❌ Error restoring field ${field.id}:`, error);
                      return null;
                    });
                  });
                  
                  await Promise.allSettled(restorePromises);
                  
                  // Small delay between batches
                  if (i + BATCH_SIZE < fieldsToRestore.length) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                  }
                }
                

              } catch (error) {
                console.error('❌ Error during bulk field restoration:', error);
              }
            })();
            break;
        }
      },

      redoLastAction: () => {
        const { undoHistory, redoHistory } = get();
        if (redoHistory.length === 0) return;

        const lastRedoAction = redoHistory[redoHistory.length - 1];
        const newRedoHistory = redoHistory.slice(0, -1);
        const newUndoHistory = [...undoHistory, lastRedoAction];

        // Keep only the last 20 undo actions
        if (newUndoHistory.length > 20) {
          newUndoHistory.shift();
        }

        set({
          undoHistory: newUndoHistory,
          redoHistory: newRedoHistory
        });

        // Execute the redo action
        const state = get();
        const { currentDocument, signatureFields } = state;
        
        switch (lastRedoAction.type) {
          case 'ADD_SIGNATURE':
            const { signatures } = state;
            const signatureToRestore = lastRedoAction.data;
            set({ signatures: [...signatures, signatureToRestore] });
            
            // Also restore the field status to 'completed' since signature was restored
            if (signatureToRestore.fieldId) {
              const relatedField = signatureFields.find(f => f.id === signatureToRestore.fieldId);
              if (relatedField && relatedField.status === 'pending') {
                set({
                  signatureFields: signatureFields.map(field =>
                    field.id === signatureToRestore.fieldId ? { ...field, status: 'completed' } : field
                  )
                });
                
                // Update field status on server as well
                (async () => {
                  try {
                    const sessionId = currentDocument?.id;
                    if (sessionId) {
                      await fetch('/api/signature-fields', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          sessionId,
                          signatureFieldId: signatureToRestore.fieldId,
                          updates: { status: 'completed' },
                        }),
                      });

                    }
                  } catch (error) {
                    console.error('❌ Error restoring field status on server during redo:', error);
                  }
                })();
              }
            }
            
            // Also restore signature to server
            (async () => {
              try {
                const sessionId = currentDocument?.id;
                if (!sessionId) {
                  console.warn('⚠️ No session ID found for signature restoration');
                  return;
                }
                

                const response = await fetch('/api/signatures', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sessionId,
                    signature: signatureToRestore,
                    type: signatureToRestore.fieldType
                  }),
                });
                if (!response.ok) {
                  console.error('❌ Failed to restore signature to database during redo');
                } else {

                }
              } catch (error) {
                console.error('❌ Error restoring signature during redo:', error);
              }
            })();
            break;
          case 'REMOVE_SIGNATURE':
            const { signatures: currentSignatures, signatureFields: redoSignatureFields } = state;
            const signatureToDelete = lastRedoAction.data;
            set({ signatures: currentSignatures.filter(sig => sig.id !== signatureToDelete.id) });
            
            // Also revert the field status back to 'pending' since signature was removed
            if (signatureToDelete.fieldId) {
              const relatedField = redoSignatureFields.find(f => f.id === signatureToDelete.fieldId);
              if (relatedField && relatedField.status === 'completed') {
                set({
                  signatureFields: redoSignatureFields.map(field =>
                    field.id === signatureToDelete.fieldId ? { ...field, status: 'pending' } : field
                  )
                });
                
                // Update field status on server as well
                (async () => {
                  try {
                    const sessionId = currentDocument?.id;
                    if (sessionId) {
                      let url = '/api/signature-fields';
                      if (typeof window !== 'undefined') {
                        const params = new URLSearchParams(window.location.search);
                        const siteIdentifier = params.get('siteIdentifier');
                        const siteCode = params.get('siteCode');
                        if (siteIdentifier || siteCode) {
                          url += '?';
                          if (siteIdentifier) url += `siteIdentifier=${encodeURIComponent(siteIdentifier)}`;
                          if (siteCode) url += `${siteIdentifier ? '&' : ''}siteCode=${encodeURIComponent(siteCode)}`;
                        }
                      }

                      await fetch(url, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          sessionId,
                          signatureFieldId: signatureToDelete.fieldId,
                          updates: { status: 'pending' },
                        }),
                      });

                    }
                  } catch (error) {
                    console.error('❌ Error reverting field status on server during redo:', error);
                  }
                })();
              }
            }
            
            // Also delete signature from server
            (async () => {
              try {

                const response = await fetch(`/api/signatures?id=${encodeURIComponent(signatureToDelete.id)}`, {
                  method: 'DELETE',
                });
                if (!response.ok) {
                  console.error('❌ Failed to delete signature from database during redo');
                } else {

                }
              } catch (error) {
                console.error('❌ Error deleting signature during redo:', error);
              }
            })();
            break;
          case 'ADD_SIGNATURE_FIELD':
            const fieldToRestore = lastRedoAction.data;
            set({ signatureFields: [...signatureFields, fieldToRestore] });
            
            // Restore to database
            (async () => {
              const sessionId = currentDocument?.id;
              if (sessionId) {
                try {

                  const response = await fetch('/api/signature-fields', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId, signatureField: fieldToRestore }),
                  });
                  if (!response.ok) {
                    console.error('❌ Failed to re-add field to database');
                  } else {

                  }
                } catch (error) {
                  console.error('❌ Error re-adding field:', error);
                }
              }
            })();
            break;
          case 'BULK_ADD_SIGNATURE_FIELDS':
            // Re-add all fields that were removed
            const { signatureFields: currentFieldsForBulkAdd } = state;
            const fieldsToAdd = lastRedoAction.data as SignatureField[];
            set({ signatureFields: [...currentFieldsForBulkAdd, ...fieldsToAdd] });
            
            // Restore all fields to database
            (async () => {
              const sessionId = currentDocument?.id;
              if (sessionId && fieldsToAdd.length > 0) {
                try {

                  const promises = fieldsToAdd.map(field =>
                    fetch('/api/signature-fields', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ sessionId, signatureField: field }),
                    })
                  );
                  await Promise.allSettled(promises);

                } catch (error) {
                  console.error('❌ Error re-adding fields:', error);
                }
              }
            })();
            break;
          case 'REMOVE_SIGNATURE_FIELD':
            const { signatureFields: currentFields } = state;
            const fieldToDelete = lastRedoAction.data;
            set({ signatureFields: currentFields.filter(field => field.id !== fieldToDelete.id) });
            
            // Delete from database
            (async () => {
              try {

                const response = await fetch(`/api/signature-fields?id=${fieldToDelete.id}`, {
                  method: 'DELETE',
                });
                if (!response.ok) {
                  console.error('❌ Failed to re-delete field from database');
                } else {

                }
              } catch (error) {
                console.error('❌ Error re-deleting field:', error);
              }
            })();
            break;
          case 'UPDATE_SIGNATURE_FIELD':
            const { signatureFields: fieldsToUpdate } = state;
            set({
              signatureFields: fieldsToUpdate.map(field =>
                field.id === lastRedoAction.data.id ? { ...field, ...lastRedoAction.data.newState } : field
              )
            });
            break;
          case 'BULK_UPDATE_SIGNATURE_FIELDS':
            // Re-apply all the updates
            const { signatureFields: fieldsToReapply } = state;
            const { updates } = lastRedoAction.data;
            set({
              signatureFields: fieldsToReapply.map((field: SignatureField) => {
                const update = updates.find((u: any) => u.id === field.id);
                return update ? { ...field, ...update.updates } : field;
              })
            });
            break;
          case 'INITIAL_FIELD_TRANSACTION':
            // Redo all parts of the transaction
            const { signatureFields: currentRedoFields } = state;
            const redoTransactionData = lastRedoAction.data;
            
            let redoFields = currentRedoFields;
            const fieldsToAddToServer: SignatureField[] = [];
            const fieldsToUpdateOnServer: Array<{id: string, updates: Partial<SignatureField>}> = [];
            
            // 1. Re-add fields that were added
            if (redoTransactionData.addedFields && redoTransactionData.addedFields.length > 0) {
              redoFields = [...redoFields, ...redoTransactionData.addedFields];
              fieldsToAddToServer.push(...redoTransactionData.addedFields);
            }
            
            // 2. Re-apply updates to fields
            if (redoTransactionData.updatedFields && redoTransactionData.updatedFields.length > 0) {
              redoFields = redoFields.map(field => {
                const update = redoTransactionData.updatedFields.find((u: any) => u.id === field.id);
                if (update) {
                  fieldsToUpdateOnServer.push({
                    id: field.id,
                    updates: { assignedUserIds: update.newState.assignedUserIds }
                  });
                  return update.newState;
                }
                return field;
              });
            }
            
            // 3. Re-apply original field status update
            if (redoTransactionData.fieldStatusUpdate) {
              redoFields = redoFields.map(field => 
                field.id === redoTransactionData.fieldStatusUpdate.id 
                  ? { ...field, status: redoTransactionData.fieldStatusUpdate.newStatus }
                  : field
              );
              
              fieldsToUpdateOnServer.push({
                id: redoTransactionData.fieldStatusUpdate.id,
                updates: { status: redoTransactionData.fieldStatusUpdate.newStatus }
              });
            }
            
            // Update local state immediately
            set({ signatureFields: redoFields });
            
            // Perform server operations asynchronously
            (async () => {
              const sessionId = currentDocument?.id;
              if (!sessionId) {
                console.warn('⚠️ No session ID found for server redo operations');
                return;
              }
              
              try {
                // Re-add fields to server
                if (fieldsToAddToServer.length > 0) {

                  const addPromises = fieldsToAddToServer.map(field =>
                    fetch('/api/signature-fields', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ sessionId, field }),
                    }).catch(error => console.error('❌ Error adding field:', field.id, error))
                  );
                  await Promise.allSettled(addPromises);
                }
                
                // Re-apply updates on server
                if (fieldsToUpdateOnServer.length > 0) {

                  const updatePromises = fieldsToUpdateOnServer.map(({ id, updates }) =>
                    fetch('/api/signature-fields', {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ sessionId, signatureFieldId: id, updates }),
                    }).catch(error => console.error('❌ Error updating field:', id, error))
                  );
                  await Promise.allSettled(updatePromises);
                }
                

              } catch (error) {
                console.error('❌ Error during server redo operations:', error);
              }
            })();
            break;

          case 'ADD_ANNOTATION':
            // Re-add annotation without adding to history
            const annotationToReAdd = lastRedoAction.data;
            const { annotations: annotationsForReAdd, currentDocument: currentDocForReAdd } = state;
            // Use spread to force new array reference and trigger re-render
            set({ annotations: [...annotationsForReAdd, annotationToReAdd] });
            
            // Re-add to database
            (async () => {
              try {

                const urlParams = new URLSearchParams(window.location.search);
                const siteIdentifier = urlParams.get('siteIdentifier');
                const siteCode = urlParams.get('siteCode');
                
                // Transform to match API expected field names
                const payload = {
                  id: annotationToReAdd.id,
                  sessionId: annotationToReAdd.sessionId || currentDocForReAdd?.id,
                  documentId: annotationToReAdd.documentId,
                  user_id: annotationToReAdd.userId,
                  pageNumber: annotationToReAdd.pageNumber,
                  type: annotationToReAdd.type,
                  content: annotationToReAdd.content,
                  position: annotationToReAdd.position,
                  color: annotationToReAdd.color,
                  strokeWidth: annotationToReAdd.strokeWidth,
                  visibility: annotationToReAdd.visibility
                };
                if (siteIdentifier) payload.siteIdentifier = siteIdentifier;
                if (siteCode) payload.siteCode = siteCode;
                
                await fetch('/api/annotations', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });

              } catch (error) {
                console.error('❌ Error re-adding annotation:', error);
              }
            })();
            break;

          case 'REMOVE_ANNOTATION':
            // Re-remove annotation without adding to history
            const annotationToReRemove = lastRedoAction.data;
            const { annotations: annotationsForReRemove, currentUser: redoCurrentUser } = state;
            // Use spread to force new array reference and trigger re-render
            set({ annotations: [...annotationsForReRemove.filter(ann => ann.id !== annotationToReRemove.id)] });
            
            // Re-delete from database
            (async () => {
              try {

                const urlParams = new URLSearchParams(window.location.search);
                const siteIdentifier = urlParams.get('siteIdentifier');
                const siteCode = urlParams.get('siteCode');
                
                const params = new URLSearchParams({
                  id: annotationToReRemove.id,
                  userId: annotationToReRemove.userId || redoCurrentUser?.id || '',
                  isAdmin: 'false'
                });
                if (siteIdentifier) params.append('siteIdentifier', siteIdentifier);
                if (siteCode) params.append('siteCode', siteCode);
                
                await fetch(`/api/annotations?${params.toString()}`, {
                  method: 'DELETE',
                });

              } catch (error) {
                console.error('❌ Error re-deleting annotation:', error);
              }
            })();
            break;

          case 'UPDATE_ANNOTATION':
            // Re-apply annotation updates
            const { id: redoAnnId, newData } = lastRedoAction.data;
            const { annotations: annotationsForReUpdate } = state;
            set({
              annotations: annotationsForReUpdate.map(ann =>
                ann.id === redoAnnId ? { ...ann, ...newData } : ann
              )
            });
            
            // Re-update in database
            (async () => {
              try {

                const urlParams = new URLSearchParams(window.location.search);
                const siteIdentifier = urlParams.get('siteIdentifier');
                const siteCode = urlParams.get('siteCode');
                
                const payload: any = {
                  id: redoAnnId,
                  updates: newData,
                };
                if (siteIdentifier) payload.siteIdentifier = siteIdentifier;
                if (siteCode) payload.siteCode = siteCode;
                
                await fetch('/api/annotations', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });

              } catch (error) {
                console.error('❌ Error re-updating annotation:', error);
              }
            })();
            break;
          
          case 'BULK_REMOVE_FIELDS':
            // Re-remove all fields that were restored
            const { signatureFields: currentFieldsForBulkRemoveRedo } = state;
            const fieldsToRemoveAgain = lastRedoAction.data; // Array of SignatureField objects
            const fieldIdsToRemoveAgain = new Set(fieldsToRemoveAgain.map((f: SignatureField) => f.id));
            set({ 
              signatureFields: currentFieldsForBulkRemoveRedo.filter(field => !fieldIdsToRemoveAgain.has(field.id)) 
            });
            
            // Remove from database again asynchronously
            (async () => {
              try {
                // Extract tenant routing params
                let siteIdentifier = null;
                let siteCode = null;
                if (typeof window !== 'undefined') {
                  const params = new URLSearchParams(window.location.search);
                  siteIdentifier = params.get('siteIdentifier');
                  siteCode = params.get('siteCode');
                }
                

                
                // Process in batches to avoid overwhelming the server
                const BATCH_SIZE = 100;
                for (let i = 0; i < fieldsToRemoveAgain.length; i += BATCH_SIZE) {
                  const batch = fieldsToRemoveAgain.slice(i, i + BATCH_SIZE);
                  
                  const deletePromises = batch.map((field: SignatureField) => {
                    let url = `/api/signature-fields?id=${field.id}`;
                    if (siteIdentifier) url += `&siteIdentifier=${encodeURIComponent(siteIdentifier)}`;
                    if (siteCode) url += `&siteCode=${encodeURIComponent(siteCode)}`;
                    
                    return fetch(url, { method: 'DELETE' }).catch(error => {
                      console.error(`❌ Error re-deleting field ${field.id}:`, error);
                      return null;
                    });
                  });
                  
                  await Promise.allSettled(deletePromises);
                  
                  // Small delay between batches
                  if (i + BATCH_SIZE < fieldsToRemoveAgain.length) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                  }
                }
                

              } catch (error) {
                console.error('❌ Error during bulk field re-deletion:', error);
              }
            })();
            break;
        }
      },

      canUndo: () => {
        const { undoHistory } = get();
        return undoHistory.length > 0;
      },

      canRedo: () => {
        const { redoHistory } = get();
        return redoHistory.length > 0;
      },

      clearHistory: () => {
        set({ undoHistory: [], redoHistory: [] });
      },

      // Document save functionality
      saveDocumentWithMarkup: async (notifyAssigned: boolean = false) => {
        const state = get();
        const { currentDocument, signatureFields, currentUser } = state;
        
        if (!currentDocument || !currentUser) {
          console.error('Cannot save: No document or user selected');
          return false;
        }

        try {
          // Prepare document data with signature fields
          const updatedDocument: PDFDocument = {
            ...currentDocument,
            signatureFields,
            status: signatureFields.length > 0 ? 'pending_signatures' : 'draft',
            lastModified: new Date().toISOString(),
            modifiedBy: currentUser.id,
            // Extract assigned signers from signature fields
            assignedSigners: Array.from(new Set(
              signatureFields.flatMap(field => field.assignedUserIds)
            )),
          };

          // Call API to save document
          const response = await fetch('/api/documents/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              document: updatedDocument,
              signatureFields,
              notifyAssigned,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to save document');
          }

          const result = await response.json();
          
          if (result.success) {
            // Update the current document in store
            set({ currentDocument: updatedDocument });
            // Clear undo/redo history after successful save
            get().clearHistory();
            return true;
          } else {
            console.error('Save failed:', result.error);
            return false;
          }

        } catch (error) {
          console.error('Error saving document:', error);
          return false;
        }
      },

      // Offline mode state
      isOffline: false,
      setIsOffline: (offline) => set({ isOffline: offline }),
      pendingOperationsCount: 0,
      setPendingOperationsCount: (count) => set({ pendingOperationsCount: count }),
    }),
    {
      name: 'pdf-annotation-store',
    }
  )
);
